import { useMemo } from 'react';
import { LayoutGrid } from 'lucide-react';
import { SearchIcon } from '../Icons';
import ItemCard from '../ItemCard';
import { PullToRefresh } from '../SwipeableList';
import { useTracking } from '../../hooks/useTracking';
import { getItemConfig } from '../../config/itemConfig';
import { calcDistance } from '../../utils/itemListHelpers';
import type { ItemType } from '../../config/itemConfig';

interface ItemListGridProps {
  isDesktop: boolean;
  items: any[];
  isLoading: boolean;
  refetch: () => void;
  itemType: ItemType;
  config: { singular: string; plural: string };
  columns: number;
  setColumns: (cols: 2 | 3 | 4) => void;
  itemsPerPage: number;
  setItemsPerPage: (n: number) => void;
  sortBy: string;
  handleSortChange: (sort: string) => void;
  totalItems: number;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  user: any;
  isAuthenticated: boolean;
  checkPermission: (params: { action: string; itemDealerId?: number }) => boolean;
  activeLocation: { lat: number; lng: number; source: string } | undefined;
  sortOptions: Array<{ value: string; label: string }>;
  onResetAll?: () => void;
  hasActiveFilters?: () => boolean;
  onLoadMore?: () => void;
}

export default function ItemListGrid({
  isDesktop,
  items,
  isLoading,
  refetch,
  itemType,
  config,
  columns,
  setColumns,
  itemsPerPage,
  setItemsPerPage,
  sortBy,
  handleSortChange,
  totalItems,
  page,
  setPage,
  totalPages,
  user,
  isAuthenticated,
  checkPermission,
  activeLocation,
  sortOptions,
  onResetAll,
  hasActiveFilters,
  onLoadMore,
}: ItemListGridProps) {
  const { trackFilter } = useTracking();
  
  const isGridMode = columns > 1;
  
  const getUserId = (): number | undefined => {
    if (!user) return undefined;
    return user.id || (user as any).user_id;
  };
  
  const renderCard = (item: any, index?: number) => {
    const itemLat = parseFloat(item.lat) || parseFloat(item.dealer_lat);
    const itemLng = parseFloat(item.lng) || parseFloat(item.dealer_lng);
    const dist = (activeLocation && itemLat && itemLng) 
      ? calcDistance(activeLocation.lat, activeLocation.lng, itemLat, itemLng)
      : undefined;
    
    const itemDealerId = item.dealer_id || item.user_id;
    const currentUserId = getUserId();
    const isOwner = itemDealerId !== undefined && currentUserId === itemDealerId;
    
    const canReserve = !isOwner && checkPermission({ action: 'reserve', itemDealerId });
    const canChat = !isOwner && checkPermission({ action: 'chat', itemDealerId });
    const canMakeOffer = !isOwner && checkPermission({ action: 'make_offer', itemDealerId });
    
    return (
      <ItemCard 
        key={item.id} 
        item={item} 
        itemType={itemType} 
        distance={dist} 
        gridMode={isGridMode}
        priority={index !== undefined && index < 4}
        canReserve={canReserve}
        canChat={canChat}
        canMakeOffer={canMakeOffer}
      />
    );
  };
  
  // Desktop Toolbar
  const renderDesktopToolbar = () => (
    <div className="px-3 py-1.5 flex items-center justify-between bg-gray-50">
      <div className="flex items-center gap-4">
        {isDesktop && totalItems > 0 ? (
          <span className="text-sm text-gray-500">
            Mostrando {((page - 1) * itemsPerPage) + 1}-{Math.min(page * itemsPerPage, totalItems)} di {totalItems} {config.plural}
          </span>
        ) : (
          <span className="text-sm text-gray-500">
            {items.length} {config.plural}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {isDesktop && (
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setPage(1);
            }}
            className="text-sm text-gray-600 bg-white border border-gray-200 rounded px-2 py-1 focus:outline-none"
          >
            <option value={12}>12 per pagina</option>
            <option value={24}>24 per pagina</option>
            <option value={48}>48 per pagina</option>
            <option value={96}>96 per pagina</option>
          </select>
        )}
        <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-1">
          <button
            onClick={() => setColumns(2)}
            className={`p-1.5 rounded ${columns === 2 ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-blue-600'}`}
            title="2 colonne"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setColumns(3)}
            className={`p-1.5 rounded ${columns === 3 ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-blue-600'}`}
            title="3 colonne"
          >
            <LayoutGrid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setColumns(4)}
            className={`p-1.5 rounded ${columns === 4 ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-blue-600'}`}
            title="4 colonne"
          >
            <LayoutGrid className="w-6 h-6" />
          </button>
        </div>
        <select
          value={sortBy}
          onChange={(e) => handleSortChange(e.target.value)}
          className="text-sm text-blue-600 bg-transparent border-none focus:outline-none cursor-pointer"
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
  
  const renderDesktopGrid = () => (
    <div className="p-3 min-w-0">
      {items.length > 0 ? (
        <div className={`grid grid-cols-1 ${columns === 2 ? 'md:grid-cols-2' : columns === 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'} gap-3 min-w-0`}>
          {items.map(renderCard)}
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-500 mt-4">Caricamento...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-16">
          <SearchIcon className="w-12 h-12 text-gray-400 mb-4" />
          <p className="text-lg text-gray-500">Nessun risultato</p>
          <p className="text-sm text-gray-400 mt-1">Prova con filtri diversi</p>
          {hasActiveFilters && hasActiveFilters() && onResetAll && (
            <button
              onClick={onResetAll}
              className="mt-4 px-4 py-2 bg-gray-100 text-blue-600 rounded-full text-sm"
            >
              Reset filtri
            </button>
          )}
        </div>
      )}
    </div>
  );
  
  const renderMobileGrid = () => (
    <div className="pb-24 px-3 pt-1 ios-full-safe">
      <PullToRefresh onRefresh={refetch}>
        {items.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map(renderCard)}
            </div>
            {onLoadMore && (
              <div className="flex justify-center py-6">
                <button
                  onClick={onLoadMore}
                  className="px-6 py-2.5 rounded-full text-[15px] font-medium transition-colors"
                  style={{
                    backgroundColor: 'var(--color-primary)',
                    color: 'white',
                  }}
                >
                  Carica altri
                </button>
              </div>
            )}
          </>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <SearchIcon className="w-12 h-12 mb-4" style={{ color: 'var(--color-text-muted)' }} />
            <p className="text-[17px]" style={{ color: 'var(--color-text-muted)' }}>Nessun risultato</p>
            <p className="text-[13px] mt-1" style={{ color: 'var(--color-text-muted)' }}>Prova con filtri diversi</p>
            {hasActiveFilters && hasActiveFilters() && onResetAll && (
              <button
                onClick={onResetAll}
                className="mt-4 px-4 py-2 rounded-full text-[15px]"
                style={{ backgroundColor: 'var(--ios-light-gray-3)', color: 'var(--color-primary)' }}
              >
                Reset filtri
              </button>
            )}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
  
  const renderMobileToolbar = () => (
    <div className="px-3 pb-1.5 flex items-center justify-end">
      <div className="flex items-center gap-2">
        <div className="hidden md:flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: 'var(--ios-light-gray-3)' }}>
          <button
            onClick={() => setColumns(2)}
            className={`p-1.5 rounded ${columns === 2 ? 'text-white' : ''}`}
            style={{ backgroundColor: columns === 2 ? 'var(--color-primary)' : 'transparent', color: columns === 2 ? 'white' : 'var(--color-text-muted)' }}
            title="2 colonne"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setColumns(3)}
            className={`p-1.5 rounded ${columns === 3 ? 'text-white' : ''}`}
            style={{ backgroundColor: columns === 3 ? 'var(--color-primary)' : 'transparent', color: columns === 3 ? 'white' : 'var(--color-text-muted)' }}
            title="3 colonne"
          >
            <LayoutGrid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setColumns(4)}
            className={`p-1.5 rounded ${columns === 4 ? 'text-white' : ''}`}
            style={{ backgroundColor: columns === 4 ? 'var(--color-primary)' : 'transparent', color: columns === 4 ? 'white' : 'var(--color-text-muted)' }}
            title="4 colonne"
          >
            <LayoutGrid className="w-6 h-6" />
          </button>
        </div>
        <select
          value={sortBy}
          onChange={(e) => handleSortChange(e.target.value)}
          className="text-[13px] text-[var(--ios-blue)] bg-transparent border-none focus:outline-none"
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
  
  return (
    <>
      {isDesktop ? (
        <>
          {renderDesktopToolbar()}
          {renderDesktopGrid()}
        </>
      ) : (
        <>
          {renderMobileToolbar()}
          {renderMobileGrid()}
        </>
      )}
    </>
  );
}
